import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { formatRelativeTime } from '@/lib/formatters';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuthenticatedFetch } from '@/hooks/useAuthenticatedFetch';
import { getCachedData, setCachedData } from '@/lib/cache';

const PAGE_SIZE = 100;

type ReferralState = 'referral' | 'converting' | 'converted';

type AdminReferral = {
  partnerUserId: string;
  referralUserId: string;
  referralState: ReferralState;
  createdAt?: number;
  convertingAt?: number;
  convertedAt?: number;
  rewardAmount?: number;
  rewardedAt?: number;
  subscriptionId?: string;
};

type ReferralsResponse = {
  referrals: AdminReferral[];
  nextToken?: string;
  count: number;
};

function formatTimestamp(timestamp?: number): string {
  if (!timestamp) return '—';
  return formatRelativeTime(timestamp * 1000);
}

function getStateBadgeVariant(
  state: ReferralState,
): 'default' | 'secondary' | 'outline' {
  switch (state) {
    case 'converted':
      return 'default';
    case 'converting':
      return 'secondary';
    case 'referral':
      return 'outline';
    default:
      return 'outline';
  }
}

export default function ReferralsListPage() {
  const authenticatedFetch = useAuthenticatedFetch();
  const [navigationHistory, setNavigationHistory] = useState<(string | null)[]>(
    [null],
  );
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [referrals, setReferrals] = useState<AdminReferral[]>([]);
  const [nextPageKey, setNextPageKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    async function fetchReferrals() {
      try {
        const currentNextToken = navigationHistory[currentPageIndex];

        // Check cache first
        const cacheKey = `referrals_${currentNextToken ?? 'first'}`;
        const cachedData = getCachedData<ReferralsResponse>(cacheKey);

        if (cachedData) {
          setReferrals(cachedData.referrals);
          setNextPageKey(cachedData.nextToken ?? null);
          setLoading(false);
          return;
        }

        setLoading(true);
        setError(null);

        // Build URL with pagination
        let url = `referrals?limit=${PAGE_SIZE}`;
        if (currentNextToken) {
          url += `&nextToken=${encodeURIComponent(currentNextToken)}`;
        }

        const response = await authenticatedFetch(url, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch referrals (${response.status})`);
        }

        const payload = (await response.json()) as ReferralsResponse;
        if (controller.signal.aborted) {
          return;
        }

        // Store in cache
        setCachedData(cacheKey, payload);

        setReferrals(payload.referrals);
        setNextPageKey(payload.nextToken ?? null);
      } catch (fetchError) {
        if (controller.signal.aborted) {
          return;
        }
        console.error(fetchError);
        setError(
          fetchError instanceof Error
            ? fetchError.message
            : 'Unable to fetch referrals.',
        );
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    fetchReferrals();

    return () => controller.abort();
  }, [authenticatedFetch, currentPageIndex, navigationHistory]);

  const hasNextPage = !!nextPageKey;
  const hasPreviousPage = currentPageIndex > 0;

  const handleNextPage = () => {
    if (!nextPageKey) return;

    // Add the next page key to navigation history if it's not already there
    if (currentPageIndex === navigationHistory.length - 1) {
      setNavigationHistory([...navigationHistory, nextPageKey]);
    }
    setCurrentPageIndex(currentPageIndex + 1);
  };

  const handlePreviousPage = () => {
    if (currentPageIndex === 0) return;
    setCurrentPageIndex(currentPageIndex - 1);
  };

  return (
    <div className="space-y-3 sm:space-y-6">
      <Breadcrumb
        items={[{ label: 'Dashboard', to: '/' }, { label: 'Referrals' }]}
      />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Referrals Overview
          </h1>
          <p className="text-muted-foreground">
            View all referral activity across partners (latest first)
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Referrals</CardTitle>
          <CardDescription>
            Showing {referrals.length} referrals on page {currentPageIndex + 1}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 sm:space-y-4">
          {error && (
            <div className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              Loading referrals...
            </div>
          )}

          {!loading && !error && referrals.length === 0 && (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              No referrals found
            </div>
          )}

          {!loading && !error && referrals.length > 0 && (
            <>
              <div className="overflow-hidden rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Partner User ID</TableHead>
                      <TableHead>Referral User ID</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Converting</TableHead>
                      <TableHead>Converted</TableHead>
                      <TableHead>Reward</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {referrals.map((referral, index) => (
                      <TableRow
                        key={`${referral.partnerUserId}-${referral.referralUserId}-${index}`}
                      >
                        <TableCell>
                          <Link
                            to={`/users/${referral.partnerUserId}`}
                            className="font-mono text-xs text-blue-600 hover:underline"
                          >
                            {referral.partnerUserId.slice(0, 8)}...
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Link
                            to={`/users/${referral.referralUserId}`}
                            className="font-mono text-xs text-blue-600 hover:underline"
                          >
                            {referral.referralUserId.slice(0, 8)}...
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={getStateBadgeVariant(
                              referral.referralState,
                            )}
                          >
                            {referral.referralState}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {formatTimestamp(referral.createdAt)}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {formatTimestamp(referral.convertingAt)}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {formatTimestamp(referral.convertedAt)}
                        </TableCell>
                        <TableCell className="text-xs">
                          {referral.rewardAmount
                            ? `${referral.rewardAmount.toLocaleString()} cr`
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center justify-between border-t pt-4">
                <Button
                  onClick={handlePreviousPage}
                  disabled={!hasPreviousPage}
                  variant="outline"
                  size="sm"
                >
                  Previous
                </Button>
                <div className="text-sm text-muted-foreground">
                  Page {currentPageIndex + 1}
                </div>
                <Button
                  onClick={handleNextPage}
                  disabled={!hasNextPage}
                  variant="outline"
                  size="sm"
                >
                  Next
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
